import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUsers, createUser, deleteUser, clearUserMessage, selectUsersLoading, selectUsersError, selectUsersMessage, selectUsersPagination } from '../features/users/userSlice';
import { selectIsAdmin } from '../features/auth/authSlice';
import UserList from '../components/users/UserList';
import UserForm from '../components/users/UserForm';
import EditUser from '../components/users/EditUser';
import { useToast } from '../contexts/ToastContext';
import BackButton from '../components/ui/BackButton';
import OmButton from '../components/ui/OmButton';

const UserManagement = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectUsersLoading);
  const isError = useSelector(selectUsersError);
  const message = useSelector(selectUsersMessage);
  const pagination = useSelector(selectUsersPagination);
  const isAdmin = useSelector(selectIsAdmin);
  const toast = useToast();

  const [view, setView] = useState('list'); 
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAllUsers({ page: currentPage, limit: itemsPerPage }));
    }
  }, [dispatch, isAdmin, currentPage, itemsPerPage]);

  useEffect(() => {
    if (isError && message) {
      toast.showError(message);
      dispatch(clearUserMessage());
    }
  }, [isError, message, toast, dispatch]);

  const handleCreateNew = () => {
    setSelectedUser(null);
    setView('create');
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setView('edit');
  };

  const handleDelete = (user) => {
    setDeleteConfirm(user);
  };

  const confirmDelete = () => {
    if (deleteConfirm) {
      dispatch(deleteUser(deleteConfirm.id))
        .unwrap()
        .then(() => {
          toast.showSuccess('User deleted successfully');
          setDeleteConfirm(null);
          // Refresh users list after deletion
          dispatch(getAllUsers({ page: currentPage, limit: itemsPerPage }));
        })
        .catch((error) => {
          toast.showError(`Failed to delete user: ${error}`);
        });
    }
  };

  const handleCreateSubmit = (userData) => {
    dispatch(createUser(userData))
      .unwrap()
      .then(() => {
        toast.showSuccess('User created successfully');
        setView('list');
        // Refresh users list after creation
        dispatch(getAllUsers({ page: currentPage, limit: itemsPerPage }));
      })
      .catch((error) => {
        toast.showError(`Failed to create user: ${error}`);
      });
  };

  const handleEditSuccess = () => {
    setView('list');
    setSelectedUser(null);
    // Refresh users list after edit
    dispatch(getAllUsers({ page: currentPage, limit: itemsPerPage }));
  };

  const handleCancel = () => {
    setView('list');
    setSelectedUser(null);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-crystal-white rounded-lg shadow-sm border border-silver-mist/20">
            <div className="px-8 py-12 text-center">
              <div className="mx-auto w-16 h-16 mb-6 flex items-center justify-center bg-error/10 border-2 border-error/20">
                <svg className="w-8 h-8 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-bold text-deep-jungle mb-3">Access Denied</h3>
              <p className="text-steel max-w-md mx-auto leading-relaxed">
                You do not have the required administrator privileges to access user management functionality.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button for list view */}
        {view === 'list' && (
          <div className="mb-6">
            <BackButton />
          </div>
        )}

        {/* Navigation Breadcrumb */}
        {view !== 'list' && (
          <div className="mb-6">
            <nav className="flex items-center space-x-2 text-sm text-steel">
              <button
                onClick={() => setView('list')}
                className="hover:text-fresh-leaf transition-colors duration-200 focus:outline-none focus:text-fresh-leaf"
              >
                User Management
              </button>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-deep-jungle font-medium">
                {view === 'create' ? 'Create User' : 'Edit User'}
              </span>
            </nav>
          </div>
        )}

        {/* Main Content */}
        <div className="bg-crystal-white rounded-lg shadow-sm border border-silver-mist/20">
          {view === 'list' && (
            <UserList
              onCreateNew={handleCreateNew}
              onEdit={handleEdit}
              onDelete={handleDelete}
              initialLoad={true}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              pagination={pagination}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
            />
          )}

          {view === 'create' && (
            <div className="p-8">
              <div className="mb-8 pb-6 border-b border-steel/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-deep-jungle">Create New User</h2>
                    <p className="text-steel mt-1">Add a new user to the system with appropriate permissions</p>
                  </div>
                  <OmButton
                    onClick={handleCancel}
                    buttonType="outlined-button"
                    buttonSize="medium"
                    leadingIcon="arrow_back"
                  >
                    Back to List
                  </OmButton>
                </div>
              </div>
              <UserForm
                onSubmit={handleCreateSubmit}
                onCancel={handleCancel}
                isLoading={isLoading}
              />
            </div>
          )}

          {view === 'edit' && selectedUser && (
            <div className="p-8">
              <div className="mb-8 pb-6 border-b border-steel/20">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-display font-bold text-deep-jungle">Edit User</h2>
                    <p className="text-steel mt-1">Update user information and permissions</p>
                  </div>
                  <OmButton
                    onClick={handleCancel}
                    buttonType="outlined-button"
                    buttonSize="medium"
                    leadingIcon="arrow_back"
                  >
                    Back to List
                  </OmButton>
                </div>
              </div>
              <EditUser
                user={selectedUser}
                onCancel={handleCancel}
                onSuccess={handleEditSuccess}
              />
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-carbon-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md mx-auto">
              <div className="bg-crystal-white rounded-lg border border-steel/20 shadow-depth">
                <div className="p-8">
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-12 h-12 bg-steel/10 rounded-lg border-2 border-steel/20 flex items-center justify-center">
                      <svg className="w-6 h-6 text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-display font-bold text-deep-jungle">Confirm Deletion</h3>
                      <p className="text-steel text-sm mt-1">This action cannot be undone</p>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <p className="text-graphite leading-relaxed">
                      Are you sure you want to delete user{' '}
                      <span className="font-medium text-deep-jungle">"{deleteConfirm.username}"</span>? This will permanently remove their account and all associated data.
                    </p>
                  </div>

                  <div className="flex justify-between gap-4">
                    <OmButton
                      onClick={() => setDeleteConfirm(null)}
                      buttonType="outlined-button"
                      buttonSize="medium"
                    >
                      Cancel
                    </OmButton>
                    <OmButton
                      onClick={confirmDelete}
                      disabled={isLoading}
                      buttonType="filled-button"
                      buttonSize="medium"
                    >
                      {isLoading ? 'Deleting...' : 'Delete User'}
                    </OmButton>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
